/* eslint-disable react/jsx-one-expression-per-line */
import * as Yup from "yup";
import { Formik } from "formik";
import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  FormHelperText,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import useAuth from "../../../hooks/useAuth";
import useMounted from "../../../hooks/useMounted";

function VerifyOTP(props) {
  const { email, onBack } = props;
  const mounted = useMounted();
  const { verifyOtpAndLogin } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <Formik
      initialValues={{
        code: "",
        submit: null,
      }}
      validationSchema={Yup.object().shape({
        code: Yup.string()
          .matches(/^\d{6}$/g, "Enter the 6-digit code")
          .required("Code is required"),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        if (mounted.current) {
          setOpen(true);
        }
        try {
          await verifyOtpAndLogin(email, values.code);
          if (mounted.current) {
            setStatus({ success: true });
            setSubmitting(false);
            setOpen(false);
          }
        } catch (err) {
          console.error(err);
          const message = err?.message || "Invalid or expired code";
          if (mounted.current) {
            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
            setOpen(false);
          }
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Box sx={{ mb: 2 }}>
            <Typography color="textPrimary" variant="h6">
              Verify your identity
            </Typography>
            <Typography color="textSecondary" variant="body2">
              We sent a 6-digit code to <strong>{email}</strong>. Enter it below
              to continue.
            </Typography>
          </Box>

          <TextField
            autoFocus
            error={Boolean(touched.code && errors.code)}
            fullWidth
            helperText={touched.code && errors.code}
            label="6-digit code"
            margin="normal"
            name="code"
            onBlur={handleBlur}
            onChange={(e) => {
              const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 6);
              e.target.value = onlyDigits;
              handleChange(e);
            }}
            value={values.code}
            variant="outlined"
            inputProps={{ inputMode: "numeric", maxLength: 6, pattern: "\\d*" }}
          />

          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>{errors.submit}</FormHelperText>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              color="primary"
              disabled={isSubmitting}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              Log In
            </Button>
            {onBack && (
              <Box sx={{ mt: 1, textAlign: "center" }}>
                <Button
                  color="inherit"
                  disabled={isSubmitting}
                  onClick={onBack}
                  variant="text"
                  sx={{ fontSize: "0.875rem" }}
                >
                  Back
                </Button>
              </Box>
            )}
            <Backdrop
              sx={{
                color: "#5271ff",
                zIndex: (theme) => theme.zIndex.drawer + 1,
              }}
              open={open}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          </Box>
        </form>
      )}
    </Formik>
  );
}

export default VerifyOTP;

VerifyOTP.propTypes = {
  email: PropTypes.string.isRequired,
  onBack: PropTypes.func,
};
